<template>
  <div>
    <div class="row">
      <!-- Left Side -->
      <div class="col-12 col-sm-7 col-md-7 col-lg-7">
        <div class="d-flex align-items-center gap-1">
          <span> Patient: </span>
          <span> <strong>{{ patient && patient.name }}</strong> </span>
        </div>

        <div class="d-inline-block my-1">
          <span> ID: </span>
          <span> <strong>{{ patientId }}</strong> </span>
          <span> Phone No: </span>
          <span> <strong> {{ patientMobile && patientMobile.value }} </strong> </span>
          <span> Age: </span>
          <span> <strong> {{ age }} </strong> </span>
          <span> Gender: </span>
          <span> <strong> {{ gender }} </strong> </span>
        </div>

        <div class="d-flex align-items-center gap-1">
          <span> Admission info: </span>
          <span> <strong>{{ selectedRegisterInfo && selectedRegisterInfo.admission_no }}</strong> </span>
        </div>

        <div class="d-inline-block my-2">
          <span> Ward / Bed / Cabin: </span>
          <span>
            <strong>
                {{ selectedRegisterInfo && selectedRegisterInfo.service_resource && selectedRegisterInfo.service_resource.name }}
            </strong>
          </span>
          <span> Consultant / Doctor: </span>
          <span>
            <strong>
              {{ selectedRegisterInfo && selectedRegisterInfo.human_resource && selectedRegisterInfo.human_resource.name }}
            </strong>
          </span>
        </div>
      </div>
      <!-- Right Side -->
      <div class="col-12 col-sm-5 col-md-5 col-lg-5">

        <div class="row h-40 mb-1">
          <div class="col-md-5 d-flex align-items-center">
            <span> Invoice Date: </span>
          </div>
          <div class="col-md-7">
            <input
              class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
              tabindex="1"
              type="date"
              placeholder=""
              v-model="formData.date"
            >
          </div>
        </div>

        <div class="row h-40 mb-1">
          <div class="col-md-5 d-flex align-items-center">
            <span> Ship from (Warehouse): </span>
          </div>

          <div class="col-md-7">
            <v-select
              placeholder="Select Warehouse Location"
              v-model="formData.location_id"
              :options="locations"
              label="text"
              :reduce="text => text.id"
            />
          </div>
        </div>

        <div class="row h-40">
          <div class="col-md-5 d-flex align-items-center">
            <span>Select Referrer:</span>
          </div>
          <div class="col-md-7">
            <div>
              <AsyncSelect
                placeholder="Select Referrer"
                v-model="formData.referrer_id"
                :api-service="fetchContactProfiles"
                :additional-query="{ type: 'agent' }"
                :format-label="formatPatientLabel"
                :reduce="referrer => referrer.id"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 col-md-10">
        <AsyncSelect
          placeholder="Select Product"
          v-model="selectedProduct"
          label="text"
          :api-service="fetchProductDropdownList"
          :format-label="option => option.text"
          :option-selected="onSelectNote"
          :additional-query="additionalProductQuery"
          :min-char-to-search="2"
          skip-api-on-open
          input-class="product-field"
          ref="productField"
        />
      </div>
      <div class="col-12 col-md-2">
        <input
          ref="quantityField"
          id="quantity"
          name="quantity"
          type="number"
          class="form-control quantity-field"
          v-model="formData.quantity"
          placeholder="quantity"
          @keydown.enter="handleProductSelection"
        />
      </div>
    </div>

    <Loader v-if="isLoader"/>

  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import handleBusinessesLocations from '@/services/modules/businessesLocations'
import handleContact from '@/services/modules/contact'
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import useDate from "@/services/utils/day";
import handleHospital from '@/services/modules/hospital'
import Loader from "@/components/atom/LoaderComponent";
import handleDropdowns from "@/services/modules/dropdowns";

const props = defineProps({
  addNewItem: {
    type: Function
  },
  tableItems: {
    type: Array
  },
  additionalProductQuery: {
    type: Object
  }
})

const $route = useRoute();
const date = useDate();
const patient = ref(null)
const isLoader = ref(false);
const quantityField = ref(null)
const productField = ref(null)
const companyId = $route.params.companyId
const companyQuery = `?company_id=${companyId}`;
const selectedData = ref({});
const formData = inject('formData');
const showError = inject("showError")
let locations = ref([]);
const selectedProduct = ref(null);
const productId = ref(null);
const selectedRegisterInfo = ref(null);

const {fetchProductById, fetchSaleOfferDiscount} = handlePurchase()
const {fetchBusinessLocationsList} = handleBusinessesLocations()
const {fetchContactProfiles, fetchSingleContactProfile} = handleContact()
const {formatPatientLabel} = useAsyncDropdownHelper()
const {calculateAge, fetchSingleNote} = handleHospital()
const { fetchProductDropdownList } = handleDropdowns();
const emit = defineEmits(['updateItemQuantityOnCache'])
const formKey = computed(() => $route.query.form_key ?? null);

const patientMobile = computed(() => {
  if (selectedData.value && selectedData.value.profile_items && selectedData.value.profile_items.length) {
    return selectedData.value.profile_items.find(item => item.field === 'mobile_no')
  }
  return null
})

const patientId = computed(() => {
  if (
    selectedData.value &&
    selectedData.value.company_roles &&
    selectedData.value.company_roles.length &&
    selectedData.value.company_roles[0].pivot
  ) {
    return selectedData.value.company_roles[0].pivot.serial_no
  }
  return null
})

const age = computed(() => {
  if (patient.value) {
    return calculateAge(patient.value.birthday)
  }
})

const gender = computed(() => {
  if (patient.value) {
    return patient.value.gender
  }
})

const onSelectNote = (note) => {
  productId.value = note.id
}

const handleProductSelection = async () => {
  if (!productId.value) {
    showError("Please select product first!");
    focusProductSelect()
    return;
  }

  if (!formData.value.quantity) {
    showError("Please enter valid quantity first!");
    return;
  }

  let quantity = Number(formData.value.quantity)

  if (!Number.isInteger(quantity)) {
    showError("The quantity can only be a number");
    formData.value.quantity = parseInt(formData.value.quantity)
    return;
  }

  let query = `${companyQuery}&location_id=${formData.value.location_id}`;
  query += `&form_key=${formKey.value}`;

  const productResp = await fetchProductById(productId.value, query);

  if (!productResp.data) {
    showError("Product not found");
    focusProductSelect()
    return;
  }

  const availableStock = productResp.data.available_stock ?? null;
  if ( availableStock !== null && availableStock < quantity) {
      showError("The quantity can not be more than stock in hand.");
      return;
  }

  const selected = productResp.data;
  let alreadyExist = false;
  let itemQuantity = quantity;

  let product = {
    name: selected.name
  }

  props.tableItems.map((item) => {
    if (item.product_id === selected.id) {
      alreadyExist = true;
      item.quantity += quantity;
      item.available_stock = availableStock;
      itemQuantity = item.quantity;
    }
  })

  const discountResp = await fetchSaleOfferDiscount(productId.value, `${companyQuery}&date=${formData.value.date}`)

  let offer = discountResp.data ?? {}

  const fixedAmount = +offer.discount_amount || 0;
  const percent = +offer.discount_percentage || 0;

  let discountAmount = fixedAmount > 0 ? fixedAmount : (
      percent ? (quantity * selected.sales_price * percent) / 100 : 0
  );

  let discountPercentage = fixedAmount > 0 ? 0 : percent;

  if (!alreadyExist) {
    props.tableItems.push({
      product_id: selected.id,
      name: selected.name,
      stock_in_hand: selected.stock_in_hand,
      description: '',
      quantity: quantity,
      available_stock: availableStock,
      rate: selected.sales_price,
      purchase_rate: selected.purchase_rate,
      discount: 0,
      vat: 0,
      sub_total: 0,
      discount_amount: discountAmount,
      discount_percent: discountPercentage,
      vat_amount: 0,
      total: 0,
      is_edit: false,
      batch_details: [],
      product: product
    })
  }
  const data = {
    product_id: selected.id,
    batch: selected.batch_number,
    quantity: alreadyExist ? itemQuantity : quantity,
    location_id: formData.value.location_id,
  }

  emit('updateItemQuantityOnCache', data)

  formData.value.quantity = null;
  productId.value = null;
  selectedProduct.value = null;
  focusProductSelect()
}

const focusProductSelect = () => {
  if (productField.value) {
    productField.value.clear();
    productField.value.dropdownRef.searchEl.focus()
  }
}

const generateDateAndInvoice = () => {
  formData.value.date = date.currentDate();
}

const resetData = () => {
  generateDateAndInvoice();
  productId.value = null;
  formData.value.location_id = locations.value[0].id
  productField.value.clear();
}

const loadIpdRegister = async () => {
  const ipdRegisterId = formData.value.ipd_register_id;
  if (!ipdRegisterId) return;
  isLoader.value = true;
  await fetchSingleNote(companyQuery, ipdRegisterId).then(res => {
    if (!res.data) return;
    selectedRegisterInfo.value = res.data;
    formData.value.contact_profile_id = res.data.patient.id;
    formData.value.service_resource_id = res.data.human_resource_id;
    patient.value = {
      id: res.data.patient.id,
      name: res.data.patient.full_name,
      birthday: res.data.patient.birthday,
      gender: res.data.patient.gender
    };
  })
  isLoader.value = false;
}

watch(patient, () => {
  if (!patient.value) return;
  const patientQuery = companyQuery + '&type=patient';
  fetchSingleContactProfile(patient.value.id, patientQuery).then(res => {
    if (res.data) selectedData.value = formData.value.contact = res.data;
  })
})

watch(productId, () => {
  if (!productId.value) return;
  if (quantityField.value) quantityField.value.focus();
})

onMounted(async () => {
  generateDateAndInvoice();
  const locationQuery = `${companyQuery}&location_type=fg_warehouse`;
  fetchBusinessLocationsList(locationQuery).then(res => {
    if (res.data) {
      locations.value = res.data
      formData.value.location_id = res.data[0].id
    }
  })
  await loadIpdRegister();
})

defineExpose({
  resetData
})
</script>
